import type { IPOI, IPoiPendingImage, IPoiTranslation, PersistedPoiDraft } from '@/entities/poi'
import type { PoiBasicInfoState, PoiLocationState } from '@/shared/api/types/poi-types'
import {
  startTransition,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { buildPoiDraft, hasPoiDraftContent } from '@/entities/poi'
import {
  useCreateTranslationDraft,
  useLocalizedTranslationJobController,
} from '@/features/localized-translation'
import {
  buildPoiBasicInfoSnapshot,
  buildPoiHydrationSignature,
  buildPoiLocationSnapshot,
  buildPoiMediaSnapshot,
  buildPoiTranslationSnapshot,
  createEmptyPoiTranslation,
  derivePoiFormValues,
  hasPoiTranslationContent,
  successfulImageUrls,
} from '@/features/poi/poi-form-model'
import { ReasonType, TRANSLATION_JOB_SCOPE } from '@/shared/constants'
import {
  DEFAULT_POI_BASIC_INFO,
  DEFAULT_POI_LOCATION,
  INITIAL_POI_BASIC_INFO,
} from '@/shared/constants/poi-form'

interface UsePoiFormStateParams {
  isEdit: boolean
  poiId: string | undefined
  tenantId: string
  /** The POI being edited; `undefined` while loading or in create mode. */
  poi: IPOI | undefined
  supportedLanguages: string[]
  formLanguageCode: string
}

/** Snapshots of each section taken when the form loaded or was last saved; "dirty" means "differs from this". */
interface PoiBaselines {
  translations: string
  location: string
  basicInfo: string
  media: string
}

function buildEmptyBaselines(): PoiBaselines {
  return {
    translations: buildPoiTranslationSnapshot([]),
    location: buildPoiLocationSnapshot(DEFAULT_POI_LOCATION),
    basicInfo: buildPoiBasicInfoSnapshot(DEFAULT_POI_BASIC_INFO, ''),
    media: buildPoiMediaSnapshot([]),
  }
}

/**
 * Owns the POI form values and everything derived from them: hydration (edit data or restored draft),
 * per-section dirty flags and the baselines the section "Save" buttons commit.
 */
export function usePoiFormState({
  isEdit,
  poiId,
  tenantId,
  poi,
  supportedLanguages,
  formLanguageCode,
}: UsePoiFormStateParams) {
  const [basicInfo, setBasicInfo] = useState<PoiBasicInfoState>(INITIAL_POI_BASIC_INFO)
  const [location, setLocation] = useState<PoiLocationState>(DEFAULT_POI_LOCATION)
  const [translations, setTranslations] = useState<IPoiTranslation[]>([])
  const [images, setImages] = useState<IPoiPendingImage[]>([])
  const [tenantDestinationId, setTenantDestinationId] = useState('')
  const [isActive, setIsActive] = useState(true)
  const [baselines, setBaselines] = useState<PoiBaselines>(buildEmptyBaselines)
  /** Signature of the last POI payload loaded into the form, so an identical render never wipes the user's edits. */
  const editHydrationSignatureRef = useRef<string | null>(null)

  const bannerUrls = useMemo(() => successfulImageUrls(images), [images])

  // * VI: Edit dùng poiId làm entity id cho job + draft; create dùng draft key theo tenant để job dịch và bản nháp gắn đúng form đang mở. | EN: Edit uses the poiId as entity id for jobs + draft; create uses a per-tenant draft key so translation jobs and the draft stay tied to the open form.
  const draftEntityId = useMemo(() => `draft:poi:${tenantId || 'unknown-tenant'}`, [tenantId])
  const translationEntityId = isEdit && poiId ? poiId : draftEntityId

  const draftInput = useMemo(
    () => ({
      basicInfo,
      location,
      translations,
      images,
      selectedTenantDestinationId: tenantDestinationId,
    }),
    [basicInfo, images, location, tenantDestinationId, translations],
  )
  const poiDraft = useMemo(() => buildPoiDraft(draftInput), [draftInput])
  const hasPoiDraft = useMemo(
    () =>
      hasPoiDraftContent(draftInput, {
        basicInfo: DEFAULT_POI_BASIC_INFO,
        location: DEFAULT_POI_LOCATION,
      }),
    [draftInput],
  )
  const hydratePoiDraft = useCallback((persistedDraft?: PersistedPoiDraft) => {
    startTransition(() => {
      setBasicInfo(persistedDraft?.basicInfo ?? DEFAULT_POI_BASIC_INFO)
      setLocation(persistedDraft?.location ?? DEFAULT_POI_LOCATION)
      setTranslations(persistedDraft?.translations ?? [])
      setImages(persistedDraft?.images ?? [])
      setTenantDestinationId(persistedDraft?.selectedTenantDestinationId ?? '')
    })
    setBaselines(buildEmptyBaselines())
  }, [])

  const { pendingSuccessJobs } = useLocalizedTranslationJobController({
    scope: TRANSLATION_JOB_SCOPE.POI,
    entityId: translationEntityId,
    audioEnabled: ReasonType.TRANSLATE_AUDIO_AVAILABLE,
  })
  const { clearCurrentJobs } = useCreateTranslationDraft({
    enabled: !isEdit,
    scope: TRANSLATION_JOB_SCOPE.POI,
    entityId: translationEntityId,
    audioEnabled: ReasonType.TRANSLATE_AUDIO_AVAILABLE,
    initSignature: `create:${tenantId}`,
    draft: poiDraft,
    hasContent: hasPoiDraft,
    onHydrate: hydratePoiDraft,
  })

  // * VI: useLayoutEffect hydrate form trước khi paint, tránh nháy khung trống khi dữ liệu POI đã có trong cache. Edit luôn hydrate từ API để draft/default cũ không đè dữ liệu POI sau refetch. | EN: useLayoutEffect hydrates before paint, avoiding an empty-frame flash when the POI is cached. Edit always hydrates from the API so stale drafts/defaults cannot override refetched data.
  useLayoutEffect(() => {
    // Without a valid poiId (route still changing) nothing may be persisted under the wrong entity key.
    if (!isEdit || !poiId || !poi) {
      return
    }
    const signature = buildPoiHydrationSignature(poi, formLanguageCode, supportedLanguages)
    if (editHydrationSignatureRef.current === signature) {
      return
    }
    editHydrationSignatureRef.current = signature

    const values = derivePoiFormValues(poi, supportedLanguages)
    startTransition(() => {
      setBasicInfo(values.basicInfo)
      setLocation(values.location)
      setTranslations(values.translations)
      setImages(values.images)
      setTenantDestinationId(values.tenantDestinationId)
    })
    // * VI: Baseline lấy từ danh sách đã merge, nếu không các tab ngôn ngữ mới thêm sẽ bị coi là "thay đổi chưa lưu" ngay khi mở form. | EN: Baselines use the merged list; otherwise newly added language tabs would read as unsaved changes the moment the form opens.
    // eslint-disable-next-line react-hooks/set-state-in-effect -- seed the editable form from the async-loaded POI (same pattern as the tour form)
    setBaselines({
      translations: buildPoiTranslationSnapshot(values.translations),
      location: buildPoiLocationSnapshot(values.location),
      basicInfo: buildPoiBasicInfoSnapshot(values.basicInfo, values.tenantDestinationId),
      media: buildPoiMediaSnapshot(poi.banner),
    })
  }, [formLanguageCode, isEdit, poi, poiId, supportedLanguages])

  // Create mode: start with one empty tab per tenant language.
  useEffect(() => {
    if (isEdit || translations.length > 0 || supportedLanguages.length < 1) {
      return
    }
    const defaultTranslations = supportedLanguages.map(createEmptyPoiTranslation)
    // eslint-disable-next-line react-hooks/set-state-in-effect -- tenant languages arrive asynchronously after mount
    setTranslations(defaultTranslations)
    setBaselines((prev) => ({
      ...prev,
      translations: buildPoiTranslationSnapshot(defaultTranslations),
    }))
  }, [isEdit, supportedLanguages, translations.length])

  const translationSnapshot = useMemo(
    () => buildPoiTranslationSnapshot(translations),
    [translations],
  )
  const locationSnapshot = useMemo(() => buildPoiLocationSnapshot(location), [location])
  const basicInfoSnapshot = useMemo(
    () => buildPoiBasicInfoSnapshot(basicInfo, tenantDestinationId),
    [basicInfo, tenantDestinationId],
  )
  const mediaSnapshot = useMemo(() => buildPoiMediaSnapshot(bannerUrls), [bannerUrls])

  // * VI: Edit: dirty = khác baseline đã lưu. Create: dirty = đã nhập bất cứ thứ gì khác giá trị mặc định (để cảnh báo khi rời trang). | EN: Edit: dirty = differs from the saved baseline. Create: dirty = anything beyond the defaults was entered (to warn on leave).
  const isTranslationDirty = isEdit
    ? translationSnapshot !== baselines.translations
    : hasPoiTranslationContent(translations)
  const isLocationDirty = isEdit
    ? locationSnapshot !== baselines.location
    : locationSnapshot !== buildPoiLocationSnapshot(DEFAULT_POI_LOCATION)
  const isBasicInfoDirty = isEdit
    ? basicInfoSnapshot !== baselines.basicInfo
    : basicInfoSnapshot !== buildPoiBasicInfoSnapshot(DEFAULT_POI_BASIC_INFO, '')
  const isMediaDirty = isEdit ? mediaSnapshot !== baselines.media : images.length > 0
  const isStatusDirty = !isEdit && !isActive
  const hasUnsavedChanges =
    isTranslationDirty || isLocationDirty || isBasicInfoDirty || isMediaDirty || isStatusDirty

  // * VI: Range nằm ở cả "Thông tin cơ bản" và "Vị trí"; đổi một bên thì đồng bộ bên kia để hai section không lệch nhau. | EN: Range lives in both "Basic info" and "Location"; changing one syncs the other so the sections never disagree.
  const handleBasicInfoChange = useCallback((next: PoiBasicInfoState) => {
    setBasicInfo(next)
    setLocation((current) => ({ ...current, range: next.range }))
  }, [])
  const handleLocationChange = useCallback((next: PoiLocationState) => {
    setLocation(next)
    setBasicInfo((current) => ({ ...current, range: next.range }))
  }, [])
  /** Categories belong to a destination, so picking another destination clears the chosen category. */
  const selectDestination = useCallback((nextDestinationId: string) => {
    setTenantDestinationId(nextDestinationId)
    setBasicInfo((current) => ({ ...current, category: '' }))
  }, [])

  /** Called after a section saved successfully, so it stops counting as dirty. */
  const commitBaseline = {
    translations: (saved: IPoiTranslation[]) => {
      setTranslations(saved)
      setBaselines((prev) => ({ ...prev, translations: buildPoiTranslationSnapshot(saved) }))
    },
    location: () => setBaselines((prev) => ({ ...prev, location: locationSnapshot })),
    basicInfo: () => setBaselines((prev) => ({ ...prev, basicInfo: basicInfoSnapshot })),
    media: () => setBaselines((prev) => ({ ...prev, media: mediaSnapshot })),
  }

  return {
    basicInfo,
    location,
    translations,
    images,
    bannerUrls,
    tenantDestinationId,
    isActive,
    setIsActive,
    setTranslations,
    setImages,
    handleBasicInfoChange,
    handleLocationChange,
    selectDestination,
    translationEntityId,
    pendingSuccessJobs,
    clearCurrentJobs,
    hasUnsavedChanges,
    isLocationDirty,
    isBasicInfoDirty,
    isMediaDirty,
    commitBaseline,
  }
}
